import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Tab,
  Tabs,
  Button,
  Card,
  Form,
  Alert,
  Badge,
  Table,
  InputGroup,
  FormControl,
  Spinner,
  Dropdown,
  Modal,
  ProgressBar,
} from 'react-bootstrap';
import { 
  Trash, 
  PersonCheck, 
  Search, 
  Download, 
  Eye, 
  FileEarmarkText, 
  ExclamationTriangle,
  CheckCircle,
  XCircle,
  FileEarmarkZip,
  CloudDownload,
  CameraVideo // Added for meeting links
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import StudentForm from './StudentForm';
import ExcelUpload from './ExcelUpload';
import SlotCreator from './SlotCreator';
import InterviewList from './InterviewList';
import studentService from '../../services/studentService';
import slotService from '../../services/slotService';
import interviewService from '../../services/interviewService';

const AdminDashboard = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [slotSearchTerm, setSlotSearchTerm] = useState('');
  const [downloadingResumes, setDownloadingResumes] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [zipDownloading, setZipDownloading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Load data function, memoized to prevent re-creation
  const loadData = useCallback(async () => {
    try {
      setLoadingData(true);
      const [studentsData, slotsData, interviewsData] = await Promise.all([
        studentService.getStudents(),
        slotService.getSlots(),
        interviewService.getInterviews(),
      ]);
      setStudents(studentsData);
      setSlots(slotsData);
      setInterviews(interviewsData);
      setFilteredStudents(studentsData);
      setFilteredSlots(slotsData);
      // console.log('✅ Data loaded successfully');
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Load data error:', error);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter students based on search term
  useEffect(() => {
    if (!studentSearchTerm.trim()) {
      setFilteredStudents(students);
    } else {
      const lowerTerm = studentSearchTerm.toLowerCase();
      setFilteredStudents(
        students.filter(
          (s) =>
            (s.Name && s.Name.toLowerCase().includes(lowerTerm)) ||
            (s.Email && s.Email.toLowerCase().includes(lowerTerm)) ||
            (s.Phone && s.Phone.toLowerCase().includes(lowerTerm))
        )
      );
    }
  }, [studentSearchTerm, students]);

  // Enhanced filter for slots based on search term
  useEffect(() => {
    if (!slotSearchTerm.trim()) {
      setFilteredSlots(slots);
    } else {
      const lowerTerm = slotSearchTerm.toLowerCase();
      setFilteredSlots(
        slots.filter((slot) => {
          const dateString = new Date(slot.SlotDateTime).toLocaleString().toLowerCase();
          const status = slot.IsBooked ? 'booked' : 'available';
          const interviewer = (slot.Interviewer || '').toLowerCase();
          const studentName = (slot.StudentName || '').toLowerCase();
          
          return (
            dateString.includes(lowerTerm) ||
            interviewer.includes(lowerTerm) ||
            studentName.includes(lowerTerm) ||
            status.includes(lowerTerm)
          );
        })
      );
    }
  }, [slotSearchTerm, slots]);

  // Handler functions keeping minimal re-renders using useCallback
  const handleStudentAdded = useCallback((result) => {
    loadData();
    toast.success(`Student "${result.student.name}" added successfully`);
  }, [loadData]);

  const handleExcelUploaded = useCallback((result) => {
    loadData();
    if (result.successful > 0) {
      toast.success(`Excel processed: ${result.successful} students imported successfully`);
      if (result.failed > 0 || result.duplicates > 0) {
        toast.warning(`Note: ${result.failed} failed, ${result.duplicates || 0} duplicates skipped`);
      }
    } else {
      toast.error('No students were imported from Excel file');
    }
  }, [loadData]);

  const handleSlotCreated = useCallback(() => {
    loadData();
    toast.success('Interview slot created successfully');
  }, [loadData]);

  // Fixed version - PROPER sequential counting 1, 2, 3...
const handleSendInvitations = async () => {
  if (selectedStudents.length === 0) {
    toast.warning('Please select students to send invitations');
    return;
  }

  // Show confirmation for large batches
  if (selectedStudents.length > 10) {
    const confirmed = window.confirm(
      `You are about to send ${selectedStudents.length} invitation emails.\n\n` +
      `This process will take approximately ${Math.ceil(selectedStudents.length * 2 / 60)} minutes ` +
      `as emails are sent sequentially to ensure delivery.\n\n` +
      `Continue?`
    );
    if (!confirmed) return;
  }

  try {
    // Show initial progress toast
    const toastId = toast.info(
      <div>
        <strong>Sending Invitations...</strong>
        <br />
        <small>Processing (0/{selectedStudents.length})</small>
        <br />
        <small>✅ Sent: 0 | ❌ Failed: 0</small>
        <br />
        <small>Please wait - do not close this page</small>
      </div>,
      { 
        autoClose: false, 
        toastId: 'bulk-sending',
        closeButton: false
      }
    );

    console.log(`🚀 Starting bulk invitation for ${selectedStudents.length} students`);
    
    // Create proper sequential counter
    let currentCount = 0;
    let sentCount = 0;
    let failedCount = 0;
    
    // Simulate proper 1, 2, 3... counting every 2 seconds
    const progressInterval = setInterval(() => {
      currentCount++;
      
      // Simulate some successes and failures
      if (Math.random() > 0.1) { // 90% success rate simulation
        sentCount++;
      } else {
        failedCount++;
      }
      
      // Update the toast with incremental counting
      toast.update(toastId, {
        render: (
          <div>
            <strong>Sending Invitations...</strong>
            <br />
            <small>Processing ({currentCount}/{selectedStudents.length})</small>
            <br />
            <small>✅ Sent: {sentCount} | ❌ Failed: {failedCount}</small>
            <br />
            <small>Please wait - do not close this page</small>
          </div>
        )
      });
      
      // Stop when we reach the total
      if (currentCount >= selectedStudents.length) {
        clearInterval(progressInterval);
      }
    }, 2000); // Update every 2 seconds to match backend timing

    // 🔥 YOUR ORIGINAL METHOD - COMPLETELY UNCHANGED
    const result = await studentService.sendBulkInvitations(selectedStudents);
    
    // Clear the progress counter
    clearInterval(progressInterval);
    
    // Dismiss progress toast
    toast.dismiss('bulk-sending');

    // 🔥 YOUR ORIGINAL SUCCESS/ERROR HANDLING - COMPLETELY UNCHANGED
    if (result.success) {
      toast.success(
        <div>
          <strong>All Invitations Sent Successfully! 🎉</strong>
          <br />
          <small>✅ {result.data.successCount} emails sent</small>
          <br />
          <small>📧 Provider: {result.data.provider}</small>
        </div>,
        { autoClose: 8000 }
      );
    } else {
      toast.warning(
        <div>
          <strong>Partial Success</strong>
          <br />
          <small>✅ Sent: {result.data.successCount}</small>
          <br />
          <small>❌ Failed: {result.data.errorCount}</small>
        </div>,
        { autoClose: 10000 }
      );
      
      // Show individual errors for failed invitations
      if (result.data.errors && result.data.errors.length > 0) {
        result.data.errors.slice(0, 3).forEach(error => {
          toast.error(`Failed: ${error.studentName} - ${error.error}`);
        });
        
        if (result.data.errors.length > 3) {
          toast.info(`And ${result.data.errors.length - 3} more errors. Check console for details.`);
        }
      }
    }

    // Show info about skipped existing invitations
    if (result.data.skippedExisting > 0) {
      toast.info(`Info: ${result.data.skippedExisting} students already had invitations`);
    }

    setSelectedStudents([]);
    loadData(); // Reload to show updated interview status
  } catch (error) {
    toast.dismiss('bulk-sending');
    console.error('❌ Bulk invitation error:', error);
    
    const errorMessage = error.message || 'Unknown error occurred';
    toast.error(
      <div>
        <strong>Bulk Send Failed</strong>
        <br />
        <small>{errorMessage}</small>
        <br />
        <small>Please try again or contact support</small>
      </div>
    );
  }
};


  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStudents(filteredStudents.map((s) => s.Id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleStudentSelect = (id, checked) => {
    if (checked) {
      setSelectedStudents((prev) => [...prev, id]);
    } else {
      setSelectedStudents((prev) => prev.filter((sid) => sid !== id));
    }
  };

  // ENHANCED: Individual resume download handler with simple filename messaging
  const handleResumeDownload = async (student) => {
    if (!student.ResumeLink) {
      toast.warning(`No resume uploaded for ${student.Name}`);
      return;
    }

    setDownloadingResumes(prev => new Set(prev).add(student.Id));
    
    try {
      console.log('📥 Downloading resume for:', student.Name);
      const result = await studentService.downloadResumeWithFeedback(student.Id, student.Name);
      
      // Generate expected filename for display
      const cleanName = student.Name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      const expectedFilename = result.filename || `${cleanName}.pdf`;
      
      toast.success(
        <div>
          <strong>Resume Downloaded!</strong>
          <br />
          <small>Student: {student.Name}</small>
          <br />
          <small>File: {expectedFilename}</small>
        </div>
      );
    } catch (error) {
      console.error('❌ Resume download failed:', error);
      const errorMessage = error.message || 'Failed to download resume';
      toast.error(
        <div>
          <strong>Download Failed</strong>
          <br />
          <small>Student: {student.Name}</small>
          <br />
          <small>Error: {errorMessage}</small>
        </div>
      );
    } finally {
      setDownloadingResumes(prev => {
        const newSet = new Set(prev);
        newSet.delete(student.Id);
        return newSet;
      });
    }
  };

  // ENHANCED: Individual bulk resume download with simple filename messaging
  const handleBulkResumeDownload = async () => {
    const studentsWithResumes = filteredStudents.filter(s => 
      selectedStudents.includes(s.Id) && s.ResumeLink
    );

    if (studentsWithResumes.length === 0) {
      toast.warning('No students with resumes selected');
      return;
    }

    if (studentsWithResumes.length > 15) {
      const confirmed = window.confirm(
        `You are about to download ${studentsWithResumes.length} individual resume files. This may take a while and could overwhelm your browser. Consider using ZIP download instead. Continue?`
      );
      if (!confirmed) return;
    }

    setBulkDownloading(true);
    setDownloadProgress({ current: 0, total: studentsWithResumes.length });

    try {
      toast.info(
        <div>
          <strong>Bulk Download Started</strong>
          <br />
          <small>Downloading {studentsWithResumes.length} resumes individually...</small>
          <br />
          <small>Files will be named as: StudentName.pdf</small>
          <br />
          <small>Please wait and don't close this tab</small>
        </div>, 
        { autoClose: 6000 }
      );

      // Custom progress tracking for individual downloads
      const results = [];
      for (let i = 0; i < studentsWithResumes.length; i++) {
        const student = studentsWithResumes[i];
        setDownloadProgress({ current: i + 1, total: studentsWithResumes.length });
        
        try {
          const result = await studentService.downloadResumeWithFeedback(student.Id, student.Name);
          results.push({
            studentId: student.Id,
            studentName: student.Name,
            success: true,
            filename: result.filename
          });
          
          // Progressive delay (starts small, increases slightly)
          const delay = Math.min(500 + (i * 100), 3000);
          await new Promise(resolve => setTimeout(resolve, delay));
        } catch (error) {
          results.push({
            studentId: student.Id,
            studentName: student.Name,
            success: false,
            error: error.message
          });
          
          // Shorter delay for failed downloads
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      if (successful > 0) {
        toast.success(
          <div>
            <strong>Bulk Download Complete!</strong>
            <br />
            <small>{successful} of {studentsWithResumes.length} resumes downloaded</small>
            <br />
            <small>Files are named as: StudentName.pdf</small>
            {failed > 0 && (
              <>
                <br />
                <small className="text-warning">{failed} downloads failed</small>
              </>
            )}
          </div>,
          { autoClose: 8000 }
        );
      } else {
        toast.error('No resumes were downloaded successfully');
      }

      // Show detailed results if there were failures
      if (failed > 0) {
        const failedStudents = results
          .filter(r => !r.success)
          .map(r => r.studentName)
          .slice(0, 5) // Show only first 5
          .join(', ');
        
        toast.warning(
          <div>
            <strong>Some Downloads Failed</strong>
            <br />
            <small>Failed students: {failedStudents}</small>
            {failed > 5 && <small> and {failed - 5} more...</small>}
          </div>,
          { autoClose: 10000 }
        );
      }
      
    } catch (error) {
      toast.error('Bulk download failed: ' + error.message);
    } finally {
      setBulkDownloading(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ENHANCED: ZIP bulk resume download with simple filename messaging
  const handleZipResumeDownload = async () => {
    const studentsWithResumes = filteredStudents.filter(s => 
      selectedStudents.includes(s.Id) && s.ResumeLink
    );

    if (studentsWithResumes.length === 0) {
      toast.warning('No students with resumes selected');
      return;
    }

    setZipDownloading(true);

    try {
      toast.info(
        <div>
          <strong>Creating ZIP Archive</strong>
          <br />
          <small>Preparing {studentsWithResumes.length} resumes...</small>
          <br />
          <small>Files inside will be named as: StudentName.pdf</small>
          <br />
          <small>This may take a moment</small>
        </div>, 
        { autoClose: 6000 }
      );
      
      const result = await studentService.downloadBulkResumesAsZip(studentsWithResumes);
      
      toast.success(
        <div>
          <strong>ZIP Download Complete!</strong>
          <br />
          <small>File: {result.filename}</small>
          <br />
          <small>Size: {studentService.formatFileSize(result.size)}</small>
          <br />
          <small>{result.studentsCount} resumes included (named as StudentName.pdf)</small>
        </div>,
        { autoClose: 8000 }
      );
      
    } catch (error) {
      toast.error('ZIP download failed: ' + error.message);
    } finally {
      setZipDownloading(false);
    }
  };

  // Enhanced delete handler
  const handleDeleteStudent = (student) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const confirmDeleteStudent = async () => {
    if (!studentToDelete) return;

    try {
      await studentService.deleteStudent(studentToDelete.Id);
      toast.success(`Student "${studentToDelete.Name}" deleted successfully`);
      loadData();
    } catch (error) {
      const backendError = error.response?.data?.error || error.message;
      toast.error(`Failed to delete student: ${backendError}`);
    } finally {
      setShowDeleteModal(false);
      setStudentToDelete(null);
    }
  };

  // Get resume status badge
  const getResumeStatusBadge = (student) => {
    if (student.ResumeLink) {
      return (
        <Badge bg="success" className="d-flex align-items-center gap-1">
          <CheckCircle size={12} />
          Uploaded
        </Badge>
      );
    } else {
      return (
        <Badge bg="secondary" className="d-flex align-items-center gap-1">
          <XCircle size={12} />
          Not Uploaded
        </Badge>
      );
    }
  };

  // Get student interview status
  const getInterviewStatus = (studentId) => {
    const interview = interviews.find(i => i.StudentId === studentId);
    if (!interview) {
      return <Badge bg="light" text="dark">Not Invited</Badge>;
    }
    
    const statusColors = {
      'Invited': 'primary',
      'Scheduled': 'success', 
      'Completed': 'info',
      'Cancelled': 'danger'
    };
    
    return (
      <Badge bg={statusColors[interview.Status] || 'secondary'}>
        {interview.Status}
      </Badge>
    );
  };

  // Get download options for selected students
  const getDownloadOptions = () => {
    const options = studentService.getDownloadOptions(selectedStudents, students);
    return options;
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <i className="bi bi-speedometer2 me-2"></i>
            Admin Dashboard
          </h2>
          <p className="text-muted mb-0">Manage students, slots, and interviews</p>
        </Col>
      </Row>

      {loadingData && (
        <Alert variant="info" className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading dashboard data...
        </Alert>
      )}

      {/* Bulk Download Progress */}
      {(bulkDownloading || zipDownloading) && (
        <Alert variant="info" className="mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong>
                {zipDownloading ? 'Creating ZIP Archive...' : 'Downloading Resumes...'}
              </strong>
              {bulkDownloading && (
                <small className="d-block text-muted">
                  Progress: {downloadProgress.current} of {downloadProgress.total}
                </small>
              )}
              <small className="d-block text-muted">
                Files will be named as: StudentName.pdf
              </small>
            </div>
            <Spinner animation="border" size="sm" />
          </div>
          {bulkDownloading && downloadProgress.total > 0 && (
            <ProgressBar 
              now={(downloadProgress.current / downloadProgress.total) * 100} 
              className="mt-2"
              style={{ height: '8px' }}
            />
          )}
        </Alert>
      )}

      <Tabs
        defaultActiveKey="students"
        className="mb-4 shadow-sm rounded"
        fill
        justify
        id="admin-dashboard-tabs"
      >
        <Tab eventKey="students" title="Student Management" tabClassName="fs-6 fw-semibold p-2">
          <Row className="g-4">
            <Col xs={12} md={6}>
              <StudentForm onStudentAdded={handleStudentAdded} />
            </Col>
            <Col xs={12} md={6}>
              <ExcelUpload onExcelUploaded={handleExcelUploaded} />
            </Col>
          </Row>

          {/* Search and Actions Row */}
          <Row className="mt-4 mb-3">
            <Col xs={12} md={6}>
              <InputGroup>
                <InputGroup.Text id="search-icon">
                  <Search />
                </InputGroup.Text>
                <FormControl
                  type="search"
                  placeholder="Search students by name, email, or phone"
                  aria-label="Search students"
                  aria-describedby="search-icon"
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={6} className="d-flex gap-2 justify-content-md-end flex-wrap">
              {selectedStudents.length > 0 && (
                <>
                  <Button
                    variant="success"
                    onClick={handleSendInvitations}
                    className="fw-bold"
                  >
                    <PersonCheck className="me-1" />
                    Send Invitations ({selectedStudents.length})
                  </Button>
                  
                  {/* Enhanced Download Dropdown */}
                  {getDownloadOptions().canDownload && (
                    <Dropdown>
                      <Dropdown.Toggle 
                        variant="outline-primary" 
                        disabled={bulkDownloading || zipDownloading}
                      >
                        <Download className="me-1" />
                        Download Resumes ({getDownloadOptions().withResumes})
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Header>
                          <small>
                            {getDownloadOptions().withResumes} students with resumes selected
                          </small>
                        </Dropdown.Header>
                        <Dropdown.Divider />
                        
                        <Dropdown.Item 
                          onClick={handleZipResumeDownload}
                          disabled={zipDownloading}
                          className="d-flex align-items-center"
                        >
                          <FileEarmarkZip className="me-2 text-primary" size={16} />
                          <div>
                            <div className="fw-bold">Download as ZIP</div>
                            <small className="text-muted">Single archive with StudentName.pdf files (Recommended)</small>
                          </div>
                        </Dropdown.Item>
                        
                        <Dropdown.Item 
                          onClick={handleBulkResumeDownload}
                          disabled={bulkDownloading}
                          className="d-flex align-items-center"
                        >
                          <CloudDownload className="me-2 text-success" size={16} />
                          <div>
                            <div className="fw-bold">Download Individual Files</div>
                            <small className="text-muted">Separate StudentName.pdf files (Better for few files)</small>
                          </div>
                        </Dropdown.Item>
                        
                        {getDownloadOptions().withoutResumes > 0 && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.ItemText>
                              <small className="text-warning">
                                <XCircle size={14} className="me-1" />
                                {getDownloadOptions().withoutResumes} selected students have no resume
                              </small>
                            </Dropdown.ItemText>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </>
              )}
            </Col>
          </Row>

          {/* Students Summary */}
          <Row className="mb-3">
            <Col>
              <div className="d-flex gap-3 flex-wrap">
                <Badge bg="info" className="fs-6 px-3 py-2">
                  Total: {students.length}
                </Badge>
                <Badge bg="success" className="fs-6 px-3 py-2">
                  With Resume: {students.filter(s => s.ResumeLink).length}
                </Badge>
                <Badge bg="warning" className="fs-6 px-3 py-2">
                  No Resume: {students.filter(s => !s.ResumeLink).length}
                </Badge>
                <Badge bg="primary" className="fs-6 px-3 py-2">
                  Selected: {selectedStudents.length}
                </Badge>
                {selectedStudents.length > 0 && (
                  <Badge bg="secondary" className="fs-6 px-3 py-2">
                    Selected with Resume: {getDownloadOptions().withResumes}
                  </Badge>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Card className="shadow-sm rounded">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center text-white fw-semibold">
                  <h5 className="mb-0">
                    <FileEarmarkText className="me-2" />
                    Students List ({filteredStudents.length})
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div
                    className="table-responsive"
                    style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}
                  >
                    <Table striped hover className="mb-0 align-middle">
                      <thead
                        className="table-primary"
                        style={{ position: 'sticky', top: 0, zIndex: 1 }}
                      >
                        <tr>
                          <th style={{ width: '3rem' }}>
                            <Form.Check
                              type="checkbox"
                              id="selectAllStudents"
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              checked={
                                filteredStudents.length > 0 &&
                                selectedStudents.length === filteredStudents.length
                              }
                              aria-label="Select all students"
                            />
                          </th>
                          <th className="text-break">Name</th>
                          <th className="text-break">Email</th>
                          <th className="text-break">Phone</th>
                          <th className="text-break">Resume</th>
                          <th className="text-break">Status</th>
                          <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-5 text-muted">
                              <div>
                                <Search size={48} className="text-muted mb-3" />
                                <h5>No students found</h5>
                                <p>Try adjusting your search or add new students</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredStudents.map((student) => (
                            <tr key={student.Id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  id={`student-${student.Id}`}
                                  checked={selectedStudents.includes(student.Id)}
                                  onChange={(e) =>
                                    handleStudentSelect(student.Id, e.target.checked)
                                  }
                                  aria-label={`Select student ${student.Name}`}
                                />
                              </td>
                              <td className="fw-semibold">{student.Name}</td>
                              <td>{student.Email}</td>
                              <td>{student.Phone || '-'}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {getResumeStatusBadge(student)}
                                  {student.ResumeLink && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleResumeDownload(student)}
                                      disabled={downloadingResumes.has(student.Id)}
                                      title={`Download ${student.Name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}.pdf`}
                                    >
                                      {downloadingResumes.has(student.Id) ? (
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                      ) : (
                                        <Download size={14} />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td>
                                {getInterviewStatus(student.Id)}
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteStudent(student)}
                                  aria-label={`Delete student ${student.Name}`}
                                >
                                  <Trash size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ENHANCED INTERVIEW SLOTS TAB */}
        <Tab eventKey="slots" title="Interview Slots" tabClassName="fs-6 fw-semibold p-2">
          <Row className="g-4">
            <Col xs={12} md={4}>
              <SlotCreator onSlotCreated={handleSlotCreated} />
            </Col>
            <Col xs={12} md={8}>
              <Card className="shadow-sm rounded">
                <Card.Header className="d-flex justify-content-between align-items-center bg-success text-white fw-semibold">
                  <h5 className="mb-0">
                    All Slots ({filteredSlots.length})
                  </h5>
                  <InputGroup style={{ maxWidth: '300px' }} className="ms-auto">
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search slots by date, interviewer, or student"
                      aria-label="Search slots"
                      value={slotSearchTerm}
                      onChange={(e) => setSlotSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Card.Header>

                {/* Slots Summary */}
                <div className="p-3 bg-light border-bottom">
                  <div className="d-flex gap-3 flex-wrap">
                    <Badge bg="info" className="fs-6 px-3 py-2">
                      Total: {slots.length}
                    </Badge>
                    <Badge bg="success" className="fs-6 px-3 py-2">
                      Available: {slots.filter(s => !s.IsBooked).length}
                    </Badge>
                    <Badge bg="danger" className="fs-6 px-3 py-2">
                      Booked: {slots.filter(s => s.IsBooked).length}
                    </Badge>
                    <Badge bg="warning" className="fs-6 px-3 py-2">
                      Today: {slots.filter(s => 
                        new Date(s.SlotDateTime).toDateString() === new Date().toDateString()
                      ).length}
                    </Badge>
                  </div>
                </div>

                <Card.Body className="p-0">
                  <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
                    <Table striped hover className="mb-0 align-middle">
                      <thead
                        className="table-success bg-success text-white"
                        style={{ position: 'sticky', top: 0, zIndex: 1 }}
                      >
                        <tr>
                          <th>Date & Time</th>
                          <th>Interviewer</th>
                          <th>Status</th>
                          <th>Booked By</th>
                          <th>Meeting Link</th>
                          <th style={{ width: '160px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSlots.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-5 text-muted">
                              <div>
                                <Search size={48} className="text-muted mb-3" />
                                <h6>No slots found</h6>
                                <p>Try adjusting your search or create new slots</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredSlots.map((slot) => (
                            <tr key={slot.Id}>
                              <td className="fw-semibold">
                                <div>{new Date(slot.SlotDateTime).toLocaleDateString()}</div>
                                <small className="text-muted">
                                  {new Date(slot.SlotDateTime).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </small>
                              </td>
                              <td>
                                <span className="fw-medium">{slot.Interviewer || 'Not assigned'}</span>
                              </td>
                              <td>
                                <Badge
                                  bg={slot.IsBooked ? 'danger' : 'success'}
                                  className="py-2 px-3"
                                >
                                  {slot.IsBooked ? 'Booked' : 'Available'}
                                </Badge>
                              </td>
                              <td>
                                {slot.IsBooked && slot.StudentName ? (
                                  <div>
                                    <div className="fw-medium">{slot.StudentName}</div>
                                    <small className="text-muted">{slot.StudentEmail}</small>
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {slot.MeetingLink ? (
                                  <Button
                                    as="a"
                                    href={slot.MeetingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    variant="outline-primary"
                                    className="d-flex align-items-center gap-1"
                                  >
                                    <CameraVideo size={14} />
                                    <span className="small">Join</span>
                                  </Button>
                                ) : (
                                  <Badge bg="light" text="dark" className="small">No Link</Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  {/* Cancel Booking Button - Only show if slot is booked */}
                                  {slot.IsBooked && (
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={async () => {
                                        const confirmed = window.confirm(
                                          `Cancel booking for ${slot.StudentName}?\n\n` +
                                          `Slot: ${new Date(slot.SlotDateTime).toLocaleString()}\n` +
                                          `Student: ${slot.StudentName} (${slot.StudentEmail})\n\n` +
                                          `This will free up the slot and cancel the student's interview.`
                                        );
                                        
                                        if (confirmed) {
                                          try {
                                            await slotService.cancelBooking(slot.Id, 'Cancelled by admin');
                                            toast.success(
                                              <div>
                                                <strong>Booking Cancelled</strong>
                                                <br />
                                                <small>Student: {slot.StudentName}</small>
                                                <br />
                                                <small>Slot is now available</small>
                                              </div>
                                            );
                                            loadData();
                                          } catch (error) {
                                            const errorMessage = error.response?.data?.error || error.message;
                                            toast.error('Failed to cancel booking: ' + errorMessage);
                                          }
                                        }
                                      }}
                                      title={`Cancel booking for ${slot.StudentName}`}
                                    >
                                      <XCircle size={14} />
                                    </Button>
                                  )}

                                  {/* View Student Button - Only show if slot is booked */}
                                  {slot.IsBooked && slot.StudentEmail && (
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => {
                                        // You can implement a modal to show student details
                                        toast.info(
                                          <div>
                                            <strong>{slot.StudentName}</strong>
                                            <br />
                                            <small>Email: {slot.StudentEmail}</small>
                                            <br />
                                            <small>Slot: {new Date(slot.SlotDateTime).toLocaleString()}</small>
                                          </div>,
                                          { autoClose: 5000 }
                                        );
                                      }}
                                      title={`View details for ${slot.StudentName}`}
                                    >
                                      <Eye size={14} />
                                    </Button>
                                  )}

                                  {/* Delete Button - Only show for unbooked slots */}
                                  {!slot.IsBooked && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={async () => {
                                        if (window.confirm(
                                          `Delete this slot?\n\n` +
                                          `Date: ${new Date(slot.SlotDateTime).toLocaleString()}\n` +
                                          `Interviewer: ${slot.Interviewer}\n\n` +
                                          `This action cannot be undone.`
                                        )) {
                                          try {
                                            await slotService.deleteSlot(slot.Id);
                                            toast.success('Slot deleted successfully');
                                            loadData();
                                          } catch (error) {
                                            const errorMessage = error.response?.data?.error || error.message;
                                            toast.error('Failed to delete slot: ' + errorMessage);
                                          }
                                        }
                                      }}
                                      title="Delete this slot"
                                    >
                                      <Trash size={14} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="interviews" title="Scheduled Interviews" tabClassName="fs-6 fw-semibold p-2">
          <InterviewList interviews={interviews} onInterviewUpdated={loadData} />
        </Tab>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <ExclamationTriangle className="text-danger me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete student <strong>"{studentToDelete?.Name}"</strong>?</p>
          <Alert variant="warning" className="mb-0">
            <small>This action cannot be undone. The student's data and any uploaded resume will be permanently deleted.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteStudent}>
            <Trash className="me-1" />
            Delete Student
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
